import {Injectable, signal} from '@angular/core';
import {BaseApi} from './base.api';
import {TAuthToken, TNullable} from '../interfaces/base';
import {EEndpointApi} from '../enums/endpoint-api.enum';
import {catchError, map, switchMap, throwError} from 'rxjs';
import {
  IAuth,
  TForgotPasswordRequest,
  IResetPasswordRequest,
  TSignInRequest,
  TSignUpRequest,
  TResetPasswordResponse,
  IAuthUser,
  IContactUs,
  IAccountResetPasswordForm,
} from '../interfaces/auth';
import {ITeam, ITeamWithBelongTo} from '../interfaces/team';
import {IFormProfileSettings} from '@shared/interfaces/profile-settings';
import {ISubscription} from '@shared/interfaces/subscription';
import {TCustomDomain} from '@shared/interfaces/custom-domain';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseApi {
  /**
   * A string variable used as a key to store or retrieve an authentication token.
   * Can be utilized in various authentication-related operations to identify
   * and manage security tokens within the application.
   */
  private tokenKey = 'authToken';
  private isAdmin = false;
  readonly authToken = signal<TNullable<string>>(null);
  readonly user = signal(null);
  readonly currentTeam = signal<ITeam>(null);
  readonly ownedTeams = signal<ITeam[]>([]);
  readonly belongToTeams = signal<ITeamWithBelongTo[]>([]);
  readonly subscription = signal<ISubscription>(null);
  readonly isOwnerCurrentTeam = signal<boolean>(false);
  readonly domains = signal<TCustomDomain[]>([]);
  /**
   * чи активна підписка на рівні команди.
   */
  readonly isActiveSubscription = signal(false);

  constructor() {
    super();
    const token = this.getToken();
    if (token) {
      this.authToken.set(token);
    }
  }
  isAdministrator() {
    return this.isAdmin;
  }
  /**
   * Зберігає наданий токен автентифікації на локальному сховищі та оновлює стан токена додатка.
   */
  saveToken(token: string): void {
    if (!token) {
      return;
    }

    localStorage.setItem(this.tokenKey, token);

    this.authToken.set(token);
  }

  removeToken(): void {
    localStorage.removeItem(this.tokenKey);
    this.authToken.set(null);
    this.isAdmin = false;
    this.user.set(null);
    this.currentTeam.set(null);
    this.subscription.set(null);
    this.isOwnerCurrentTeam.set(false);
    this.isActiveSubscription.set(false);
  }

  private getToken(): TNullable<string> {
    return localStorage.getItem(this.tokenKey);
  }

  public signIn(form: TSignInRequest, _secret: string = null) {
    return this.post<{token: string}>(EEndpointApi.SignIn, {...form, _secret}).pipe(
      map((response) => {
        this.saveToken(response.token);
        return response;
      }),
      switchMap((response) => {
        return this.getProfile().pipe(
          map(() => {
            return {...response};
          })
        );
      })
    );
  }

  public signUp(form: TSignUpRequest, _secret: string = null) {
    return this.post<{token?: TAuthToken}>(EEndpointApi.SignUp, {...form, _secret}).pipe(
      map((response) => {
        this.saveToken(null);
        return response;
      })
    );
  }

  public forgotPassword(form: TForgotPasswordRequest, _secret: string = null) {
    return this.post<{status: string}>(EEndpointApi.ForgotPassword, {...form, _secret});
  }

  public resetPassword(form: IResetPasswordRequest, _secret: string = null) {
    // ApiAuthResetPasswordResponse200POST
    return this.post<TResetPasswordResponse>(EEndpointApi.ResetPassword, {
      ...form,
      _secret,
    }).pipe(
      map((response) => {
        if (response?.token) {
          this.saveToken(response.token);
        }
        return response;
      })
    );
  }

  public getProfile() {
    return this.get<IAuth>(EEndpointApi.Profile).pipe(
      map((response) => {
        this.isAdmin = response?.isAdmin || false;

        this.isActiveSubscription.set(response?.isActiveSubscription === true);

        if ('token' in response) {
          this.saveToken(response?.token);
        }

        if ('user' in response) {
          this.user.set(response?.user);

          // Bugsnag.setUser(response?.user.id);
        }

        if ('currentTeam' in response) {
          this.currentTeam.set(response?.currentTeam);
        }

        if ('ownedTeams' in response) {
          this.ownedTeams.set(response?.ownedTeams);
        }

        if ('subscription' in response) {
          this.subscription.set(response?.subscription);
        }

        if ('domains' in response) {
          this.domains.set(response.domains);
        }

        this.isOwnerCurrentTeam.set(response?.isOwnerCurrentTeam == true);

        if ('belongToTeams' in response) {
          this.belongToTeams.set(response?.belongToTeams);
        }

        return response;
      }),

      catchError((response) => {
        if ([401, 403].includes(response.status)) {
          this.removeToken();
          this.user.set(null);
        }
        return throwError(() => response?.error);
      })
    );
  }

  public signOut() {
    return this.post<boolean>(EEndpointApi.SignOut, {}).pipe(
      map((response) => {
        this.removeToken();
        return response;
      })
    );
  }

  public updateProfile(data: Partial<IFormProfileSettings>) {
    return this.post<IAuthUser>(EEndpointApi.Profile, data).pipe(
      map((response) => {
        this.user.set(response);
        return response;
      })
    );
  }

  /**
   * Sends a contact us form to the API endpoint.
   *
   * @param form - The contact us form data to be submitted
   * @return - A promise that resolves when the form is successfully submitted
   */
  public contactUs(form: IContactUs) {
    return this.post<void>(EEndpointApi.ContactUs, form);
  }

  public accountResetPassword(form: Partial<IAccountResetPasswordForm>) {
    return this.post<{message: string}>(EEndpointApi.AccountResetPassword, form);
  }

  public deleteAccountSendCode() {
    return this.post<{code: string}>(EEndpointApi.DeleteAccountSendCode, {});
  }

  public deleteAccountConfirmation(code: string, reason?: string, customReason?: string) {
    return this.post<boolean>(EEndpointApi.DeleteAccountConfirmation, {code, reason, customReason});
  }
}
